import { useRef, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeGetTask, makeGetTaskSubtasks, makeGetTaskActiveSubtasks, makeGetTaskCompletedSubtasks } from '../selectors/tasks';
import { makeGetFetchedState, makeGetLoadingState } from '../selectors/global';

import { fetchTasks, createTask, createTasks, updateTask, moveTask, removeTask, addTaskLabels } from '../actions/tasks';

import { isPersonalTask } from 'utils/task';
import { reorderArrayById } from 'utils/helpers'
import { getNewOrder } from 'utils/dnd'

import { trackEvent } from '../services/events-tracker-service';
import { getCurrentUserId } from '../services/store-service';
import { getUserPreference, setUserPreference } from '../services/user-preferences-service';

import CanUser from './can-user';
import CreateListTaskItem from 'components/CreateListTaskItem';
import TaskListItem from 'components/TaskListItem';
import ResourceList from 'components/ResourceList';
import SubtasksFetcher from 'components/SubtasksFetcher';

import ResourceListItemPlaceholder from './placeholders/ResourceListItem.placeholder';

import IconArrowDown from 'icons/icon-arrow-down';
import IconArrowUp from 'icons/icon-arrow-up';

const Subtasks = ({ taskId, parentTaskIsPersonal, loading, activeSubtasks, completedSubtasks, onItemClick }) => {
    const { t } = useTranslation('task');

    const clickTimeout = useRef(null);
    const [sortedItems, setSortedItems] = useState(null);

    const computedItems = useMemo(() => {
        return sortedItems || activeSubtasks
    }, [activeSubtasks, sortedItems]);

    const taskProps = useMemo(() => ({
        task: taskId,
        type: parentTaskIsPersonal ? 'personal' : undefined
    }), [taskId, parentTaskIsPersonal]);

    const defaultCollapsed = useMemo(() => {
        return getUserPreference('task_subtasks_collapsed');
    }, []);

    const handleToggle = expanded => {
        setUserPreference('task_subtasks_collapsed', !expanded);
    }

    const handleSubtaskClick = id => {
        if (onItemClick) {
            if (clickTimeout.current) {
                clearTimeout(clickTimeout.current);
                clickTimeout.current = null;
                return;
            }

            clickTimeout.current = setTimeout(() => {
                onItemClick(id);
            }, 200);
        }
    }

    const handleMoveItem = (dragId, targetId, save) => {
        const sorted = reorderArrayById(computedItems, dragId, targetId);

        setSortedItems(sorted);

        if (save) {
            const nextIndex = sorted.findIndex(({ id }) => id === dragId);
            const order = getNewOrder(sorted[nextIndex - 1], sorted[nextIndex + 1], sorted);

            moveTask(dragId, {
                type: 'task',
                id: taskId,
                order
            });

            updateTask(dragId, { order }, true);

            setSortedItems(null);
        }
    }

    if (loading) {
        return (
            <ResourceList>
                <SubtasksFetcher key={taskId} taskId={taskId} />

                <ResourceListItemPlaceholder key={1} />

                <ResourceListItemPlaceholder key={2} />

                <ResourceListItemPlaceholder key={3} />
            </ResourceList>
        )
    }

    return (
        <>
            <SubtasksFetcher key={taskId} taskId={taskId} />

            <ResourceList>
                {
                    computedItems.map((subtask, index) =>
                        <TaskListItem
                            key={subtask.id}
                            id={subtask.id}
                            index={index}
                            task={subtask}
                            editOnDoubleClick={true}
                            showAssignees={true}
                            showComplete={true}
                            draggable={true}
                            dragType={taskId}
                            withTaskGroupIcon={true}
                            moveItem={handleMoveItem}
                            onClick={handleSubtaskClick}
                        />
                    )
                }

                <CanUser type="createSubtask">
                    <CreateListTaskItem
                        placeholder={ t('Add a new subtask') }
                        autoCreate={true}
                        taskProps={taskProps}
                        // onCreate={handleTaskCreate}
                    />
                </CanUser>
            </ResourceList>

            {
                completedSubtasks.length ? (
                    <ResourceList
                        defaultCollapsed={defaultCollapsed}
                        toggleElement={`${ completedSubtasks.length } Completed Subtasks`}
                        onToggle={handleToggle}
                    >
                        {
                            completedSubtasks.map(subtask =>
                                <TaskListItem
                                    key={subtask.id}
                                    id={subtask.id}
                                    task={subtask}
                                    showAssignees={true}
                                    showComplete={true}
                                    withTaskGroupIcon={true}
                                    onClick={handleSubtaskClick}
                                />
                            )
                        }
                    </ResourceList>
                ) : null
            }
        </>
    );
}


const makeMapStateToProps = () => {
    const getTask = makeGetTask();
    const getTaskActiveSubtasks = makeGetTaskActiveSubtasks();
    const getTaskCompletedSubtasks = makeGetTaskCompletedSubtasks();
    const getTasksLoading = makeGetLoadingState();

    return ({ tasks }, ownProps) => ({
        parentTaskIsPersonal: isPersonalTask(getTask(tasks.items, ownProps.taskId)),
        activeSubtasks: getTaskActiveSubtasks(tasks.items, ownProps.taskId),
        completedSubtasks: getTaskCompletedSubtasks(tasks.items, ownProps.taskId),
        loading: getTasksLoading(tasks.fetchInfo, 'task', ownProps.taskId)
    });
};


export default connect(makeMapStateToProps)(Subtasks);
